import React, { useMemo, useState } from 'react';
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Icon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Flex,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  InputGroup,
  Input,
  InputRightElement,
  IconButton,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { FiCalendar, FiPlus, FiCopy, FiCheck, FiMail, FiLinkedin, FiLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import EventCard from './EventCard';
import EventPreviewCard from './EventPreviewCard';
import EventDetailsModal from './EventDetailsModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { useEvents, useDeleteEvent } from '../../hooks/useEvents';

const tabStyles = {
  fontSize: 'sm',
  fontWeight: '500',
  color: '#a3a3a3',
  px: 3,
  py: 3,
  borderBottom: '2px solid transparent',
  mb: '-1px',
  _selected: { color: '#fafafa', borderBottomColor: '#fafafa', fontWeight: 600 },
  _hover: { color: '#fafafa' },
  _focus: { boxShadow: 'none' },
};

const EmptyState = ({ label, sub, onCreate }) => (
  <Box py={{ base: 8, md: 10 }} px={{ base: 4, md: 6 }} textAlign="center">
    <VStack spacing={4}>
      <Icon as={FiCalendar} boxSize={10} color="rgba(255,255,255,0.25)" strokeWidth={1} />
      <Box>
        <Text fontSize="md" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
          {label}
        </Text>
        {sub && (
          <Text fontSize="sm" color="#a3a3a3" mt={1} lineHeight="1.6">
            {sub}
          </Text>
        )}
      </Box>
      {onCreate && (
        <Button
          onClick={onCreate}
          leftIcon={<FiPlus />}
          bg="#fafafa"
          color="#0a0a0a"
          borderRadius="full"
          fontWeight="600"
          fontSize="sm"
          h="36px"
          mt={1}
          _hover={{ opacity: 0.85 }}
        >
          Create Event
        </Button>
      )}
    </VStack>
  </Box>
);

const EventGrid = ({ events, onDelete, onEdit, onShare, onView, emptyLabel, emptySub, onCreate }) => {
  if (!events || events.length === 0) {
    return <EmptyState label={emptyLabel} sub={emptySub} onCreate={onCreate} />;
  }
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} px={1}>
      {events.map((event) => (
        <EventCard key={event.id} event={event} onDelete={onDelete} onEdit={onEdit} onShare={onShare} onView={onView} />
      ))}
    </SimpleGrid>
  );
};

const ShareModal = ({ event, isOpen, onClose }) => {
  const toast = useToast();
  const shareUrl = event ? `https://app.gillyreach.com/e/${event.id}` : '';
  const { hasCopied, onCopy } = useClipboard(shareUrl);

  const channels = [
    {
      key: 'email',
      label: 'Email',
      icon: FiMail,
      href: event
        ? `mailto:?subject=${encodeURIComponent(`You're invited: ${event.title}`)}&body=${encodeURIComponent(`Join us for ${event.title}.\n\n${shareUrl}`)}`
        : '#',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: FiLinkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(0,0,0,0.65)" backdropFilter="blur(4px)" />
      <ModalContent bg="#0d0d0d" border="1px solid rgba(255,255,255,0.1)" borderRadius="18px" mx={4} maxW="440px">
        <ModalHeader color="#fafafa" fontSize="md" fontWeight="700" pb={1}>
          Share event
          <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="400" mt={0.5}>
            This is the invite your guests will receive.
          </Text>
        </ModalHeader>
        <ModalCloseButton color="#a3a3a3" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} />
        <ModalBody pb={6}>
          {/* The real invite card */}
          <Box py={2} mb={5}>
            {event && <EventPreviewCard data={event} mode="invite" />}
          </Box>

          <Text fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.5)" mb={2} letterSpacing="0.04em" textTransform="uppercase">
            Event link
          </Text>
          <InputGroup mb={5}>
            <Input
              value={shareUrl}
              isReadOnly
              fontSize="sm"
              bg="#0a0a0a"
              borderColor="rgba(255,255,255,0.12)"
              color="#fafafa"
              _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
              _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
            />
            <InputRightElement>
              <IconButton
                size="sm"
                aria-label="Copy link"
                icon={hasCopied ? <FiCheck size={15} /> : <FiCopy size={15} />}
                variant="ghost"
                color={hasCopied ? 'rgba(122,226,161,0.95)' : '#a3a3a3'}
                onClick={onCopy}
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
              />
            </InputRightElement>
          </InputGroup>

          <Text fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.5)" mb={2} letterSpacing="0.04em" textTransform="uppercase">
            Share via
          </Text>
          <HStack spacing={3}>
            <Button
              flex="1"
              leftIcon={<FiLink size={15} />}
              onClick={() => {
                onCopy();
                toast({ title: 'Link copied', status: 'success', duration: 1800, isClosable: true });
              }}
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="10px"
              fontSize="sm"
              fontWeight="600"
              _hover={{ opacity: 0.85 }}
              _focus={{ boxShadow: 'none' }}
            >
              Copy link
            </Button>
            {channels.map((c) => (
              <IconButton
                key={c.key}
                as="a"
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Share via ${c.label}`}
                icon={<c.icon size={16} />}
                bg="rgba(255,255,255,0.06)"
                color="#fafafa"
                border="1px solid rgba(255,255,255,0.12)"
                borderRadius="10px"
                _hover={{ bg: 'rgba(255,255,255,0.12)' }}
                _focus={{ boxShadow: 'none' }}
              />
            ))}
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CountBadge = ({ n, color }) =>
  n > 0 ? (
    <Badge
      bg={color.bg}
      color={color.text}
      border={`1px solid ${color.border}`}
      borderRadius="full"
      fontSize="10px"
      fontWeight="600"
      px={2}
      textTransform="none"
    >
      {n}
    </Badge>
  ) : null;

const PENDING_STATES = ['pending', 'changes_requested', 'rejected'];

const EventsList = () => {
  const { data: events = [] } = useEvents();
  const deleteMutation = useDeleteEvent();
  const navigate = useNavigate();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [shareEvent, setShareEvent] = useState(null);
  const [detailsEvent, setDetailsEvent] = useState(null);

  const { pendingReview, approved, drafts } = useMemo(() => ({
    // In review = submitted and not yet approved (covers pending, changes
    // requested, and reject — all still need the owner's attention).
    pendingReview: events.filter((e) => PENDING_STATES.includes(e.complianceStatus)),
    approved: events.filter((e) => e.complianceStatus === 'approved'),
    // Drafts have never been submitted (no compliance status yet).
    drafts: events.filter((e) => !e.complianceStatus),
  }), [events]);

  const confirmDelete = () => {
    if (pendingDeleteId) deleteMutation.mutate(pendingDeleteId);
    setPendingDeleteId(null);
  };

  const goCreate = () => navigate('/events/new');
  const goEdit = (id) => navigate(`/events/${id}/edit`);

  return (
    <>
      <Tabs variant="unstyled" isLazy>
        <TabList mb={5} px={1} pt={1} borderBottom="1px solid rgba(255,255,255,0.08)" gap={1}>
          <Tab {...tabStyles}>
            <Flex align="center" gap={2}>
              <span>Pending Review</span>
              <CountBadge
                n={pendingReview.length}
                color={{ bg: 'rgba(251,191,36,0.12)', text: 'rgba(251,191,36,0.9)', border: 'rgba(251,191,36,0.25)' }}
              />
            </Flex>
          </Tab>
          <Tab {...tabStyles}>
            <Flex align="center" gap={2}>
              <span>Approved</span>
              <CountBadge
                n={approved.length}
                color={{ bg: 'rgba(122,226,161,0.12)', text: 'rgba(122,226,161,0.9)', border: 'rgba(122,226,161,0.25)' }}
              />
            </Flex>
          </Tab>
          <Tab {...tabStyles}>Drafts</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0} pt={2}>
            <EventGrid
              events={pendingReview}
              onDelete={setPendingDeleteId}
              onEdit={goEdit}
              emptyLabel="No events pending review"
              emptySub="Create an event and submit it — your compliance team will be notified to review the materials."
              onCreate={goCreate}
            />
          </TabPanel>
          <TabPanel px={0} pt={2}>
            <EventGrid
              events={approved}
              onDelete={setPendingDeleteId}
              onShare={setShareEvent}
              onView={setDetailsEvent}
              emptyLabel="No approved events yet"
              emptySub="Events move here once compliance signs off. You can then confirm the date and book the venue."
            />
          </TabPanel>
          <TabPanel px={0} pt={2}>
            <EventGrid
              events={drafts}
              onDelete={setPendingDeleteId}
              onEdit={goEdit}
              emptyLabel="No drafts"
              emptySub="Save an event as a draft to continue working on it before submitting for review."
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={confirmDelete}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmText="Delete"
      />

      <ShareModal
        event={shareEvent}
        isOpen={Boolean(shareEvent)}
        onClose={() => setShareEvent(null)}
      />

      <EventDetailsModal
        event={detailsEvent}
        isOpen={Boolean(detailsEvent)}
        onClose={() => setDetailsEvent(null)}
      />
    </>
  );
};

export default EventsList;
